/**
 * The app's default transform gizmo on the Object Editor's selected 2D
 * shapes: its view-space variant (orthographic camera) — move arrows + free
 * center, rotation ring, per-axis and uniform scale — writes the shapes'
 * geometry. A drag is one editor transaction = one undo step.
 */

import * as THREE from 'three';
import type { ObjectDefinitionSpec, Vec2 } from 'shared-types';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmoBinding';
import type { Snapshot } from '../../../potree/personas/tools/ToolUndoRedoManager';
import type { EditorStore } from '../document/EditorStore';
import { resolveAuthoredShapes } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { shapeBounds, unionBounds } from '../document/geometry';
import { rotateShapes, roundCoord, scaleShapes, translateShapes } from '../document/specOps';

/** Rotation snaps to 15° steps when within 3° of one. */
const ROTATION_STEP = Math.PI / 12;
const ROTATION_SNAP = Math.PI / 60;

export class ShapesGizmoBinding implements TransformGizmoBinding {
  private ids: readonly string[] = [];
  /** Grid step for move snapping (0 = off) and a point it runs through. */
  private grid = 0;
  private gridOrigin: Vec2 = [0, 0];
  /** Drag baseline; `moved` = the translation applied so far (the gizmo follows it). */
  private base: { spec: ObjectDefinitionSpec; center: Vec2; moved: Vec2 } | null = null;

  constructor(private readonly store: EditorStore) {}

  /** The shapes the gizmo transforms and the current grid; not changed mid-drag. */
  configure(ids: readonly string[], grid: number, gridOrigin: Vec2): void {
    if (this.base) return;
    this.ids = ids;
    this.grid = grid;
    this.gridOrigin = gridOrigin;
  }

  /** Center of the selection's bounds (the gizmo's pivot). */
  private center(): Vec2 | null {
    const { spec } = this.store.getState();
    const bounds = unionBounds(resolveAuthoredShapes(spec).filter((s) => this.ids.includes(s.id)).map(shapeBounds));
    return bounds ? [(bounds.min[0] + bounds.max[0]) / 2, (bounds.min[1] + bounds.max[1]) / 2] : null;
  }

  /**
   * Mid-drag the pivot is the start center plus the applied move (not the
   * live bounds: rotating would wobble it), so the gizmo travels with a move
   * and stays put while rotating or scaling. The gizmo's drag math uses its
   * own frozen baseline, so following along doesn't feed back.
   */
  getFrame(): GizmoFrame | null {
    const center: Vec2 | null = this.base ? [this.base.center[0] + this.base.moved[0], this.base.center[1] + this.base.moved[1]] : this.center();
    return center ? { position: new THREE.Vector3(center[0], center[1], 0), quaternion: new THREE.Quaternion() } : null;
  }

  getSpace(): GizmoSpace {
    return 'auto';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    return null;
  }

  beginDrag(): void {
    const center = this.center();
    if (!center) return;
    this.base = { spec: this.store.getState().spec, center, moved: [0, 0] };
    this.store.begin();
  }

  /** Moves snap the selection's center to the grid; Shift/Ctrl (override) moves freely. */
  applyTranslation(worldDelta: THREE.Vector3, override: boolean): void {
    const base = this.base;
    if (!base) return;
    let du = worldDelta.x;
    let dn = worldDelta.y;
    const grid = this.grid;
    if (!override && grid > 0) {
      const [ou, on] = this.gridOrigin;
      du = roundCoord(ou + Math.round((base.center[0] + du - ou) / grid) * grid - base.center[0]);
      dn = roundCoord(on + Math.round((base.center[1] + dn - on) / grid) * grid - base.center[1]);
    }
    base.moved = [du, dn];
    this.store.apply(() => translateShapes(base.spec, this.ids, du, dn));
  }

  /** Soft 15° steps; Shift/Ctrl (override) rotates freely. */
  applyRotation(worldAxis: THREE.Vector3, angle: number, override: boolean): void {
    const base = this.base;
    if (!base) return;
    // The view ring's axis points along the view direction: its sign tells the turn direction in the plan.
    let counterClockwise = angle * (worldAxis.z < 0 ? -1 : 1);
    const step = Math.round(counterClockwise / ROTATION_STEP) * ROTATION_STEP;
    if (!override && Math.abs(counterClockwise - step) <= ROTATION_SNAP) counterClockwise = step;
    this.store.apply(() => rotateShapes(base.spec, this.ids, base.center, counterClockwise));
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number): void {
    const base = this.base;
    if (!base || !(factor > 1e-4)) return;
    const su = axes.includes('x') ? factor : 1;
    const sn = axes.includes('y') ? factor : 1;
    this.store.apply(() => scaleShapes(base.spec, this.ids, base.center, su, sn));
  }

  /** The editor store keeps the undo history: nothing for a tool undo stack. */
  commitDrag(): Snapshot | null {
    this.base = null;
    this.store.commit();
    return null;
  }

  cancelDrag(): void {
    this.base = null;
    this.store.rollback();
  }
}
